import { api, apiRequest, USE_MOCK_DATA } from '@/lib/api';
import {
  bytesToBase64,
  type ImportRequest,
  type ImportResponse,
  type InspectResponse,
} from '@/data/lead-import-model';

/* ----------------------------------------------------------------------------
 * Lead import — network edge. Owner-only, SESSION-COOKIE authenticated via the
 * shared `api` client (credentials:'include' + CSRF double-submit). The .xlsx
 * is read in the browser and sent base64 in the JSON body; the server parses it
 * (incl. hyperlink targets) — no spreadsheet library ships in the bundle.
 *
 *   inspectWorkbook(file)        POST /outreach/import/inspect
 *   previewImport(req)           POST /outreach/import   (dry_run:true, no writes)
 *   commitImport(req, idemKey)   POST /outreach/import   (dry_run:false; the SAME
 *                                idempotency key on a retry replays the original
 *                                result — never a second import)
 * NO SEND anywhere. Mock mode returns a canned inspect/preview from fixture
 * headers so the wizard can be exercised without a backend.
 * -------------------------------------------------------------------------- */

export async function readFileBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  return bytesToBase64(new Uint8Array(buf));
}

export async function inspectWorkbook(fileName: string, fileB64: string): Promise<InspectResponse> {
  if (USE_MOCK_DATA) return mockInspect(fileName);
  return api.post<InspectResponse>('/outreach/import/inspect', { file_name: fileName, file_b64: fileB64 });
}

export async function previewImport(req: ImportRequest): Promise<ImportResponse> {
  if (USE_MOCK_DATA) return mockRun({ ...req, dry_run: true });
  return api.post<ImportResponse>('/outreach/import', { ...req, dry_run: true });
}

export async function commitImport(req: ImportRequest, idempotencyKey: string): Promise<ImportResponse> {
  if (USE_MOCK_DATA) return mockRun({ ...req, dry_run: false });
  return apiRequest<ImportResponse>('/outreach/import', { method: 'POST', body: { ...req, dry_run: false }, idempotencyKey });
}

/** Fresh idempotency key for ONE commit decision; reused verbatim on retry. */
export function newImportKey(): string {
  return `import-${crypto.randomUUID()}`.slice(0, 64);
}

// ---- mock (dev only; USE_MOCK_DATA) ---------------------------------------
const MOCK_HEADERS = ['ID', 'Day', 'Priority', 'Business', 'Category', 'Area / route', 'Address', 'Current booking platform',
  'Website / direct booking', 'Phone', 'Email', 'Decision-maker', 'Visit approach', 'Verification', 'Booking evidence link',
  'Platform evidence notes', 'Google Maps', 'Google search', 'Business / address source', 'Public listing links', 'Sales status',
  'Visit date', 'Outcome / current pain', 'Follow-up date', 'Subscribed?', 'Content shoot booked?', 'Notes'];
const MOCK_MAP: Record<string, string> = { ID: 'import_row_ref', Day: 'route_day', Priority: 'priority', Business: 'business_name', Category: 'modality',
  'Area / route': 'city_region', Address: 'address', 'Current booking platform': 'booking_platform', 'Website / direct booking': 'website', Phone: 'phone',
  Email: 'business_email', 'Decision-maker': 'decision_maker', 'Visit approach': 'visit_approach', Verification: 'verification',
  'Booking evidence link': 'platform_evidence_url', 'Platform evidence notes': 'platform_evidence_note', 'Google Maps': 'link_maps', 'Google search': 'link_search',
  'Business / address source': 'link_source', 'Public listing links': 'link_listings', 'Sales status': 'status', 'Visit date': 'visit_date',
  'Outcome / current pain': 'outcome_note', 'Follow-up date': 'next_action_date', 'Subscribed?': 'subscribed', 'Content shoot booked?': 'content_shoot_booked', Notes: 'fit_notes' };

function mockInspect(fileName: string): InspectResponse {
  const fields = Object.fromEntries(Object.values(MOCK_MAP).map((f) => [f, { label: f.replace(/_/g, ' '), existing: ['business_name', 'modality', 'city_region', 'website', 'business_email', 'status', 'next_action_date', 'fit_notes', 'link_source'].includes(f) }]));
  fields.ignore = { label: '— ignore this column —', existing: true };
  return {
    file_name: fileName,
    sheets: [
      { name: 'Week 1 plan', header_row: 5, data_rows: 16, headers: [{ col: 'A', label: 'Day', samples: ['Monday 5 Oct'], has_links: false }, { col: 'B', label: 'Area circuit', samples: ['Broadhurst Gardens → Heritage Lane'], has_links: false }] },
      { name: 'Prospects', header_row: 6, data_rows: 150, headers: MOCK_HEADERS.map((label, i) => ({ col: String.fromCharCode(65 + (i % 26)), label, samples: mockSamples(label), has_links: /Maps|search|source|evidence link|Website/.test(label) })) },
    ],
    suggested_sheet: 'Prospects',
    suggested_mapping: MOCK_MAP as InspectResponse['suggested_mapping'],
    preset: { key: 'booksesh-prospects-week1', label: 'BookSesh 150 prospects (Week 1) workbook', workbook_key: 'booksesh-150-prospects-week1', applies: true },
    fields: fields as InspectResponse['fields'],
    visit_approach_options: { walk_in: 'Walk in', phone_call: 'Phone Call', social_fb: 'Social media – FB', social_ig: 'Social media – IG', social_tiktok: 'Social media – TikTok', campaign: 'Campaign', own_source: 'Own Source' },
    status_options: { DISCOVERED: 'Not visited', CONTACTED: 'Visited / contacted' },
  };
}
function mockSamples(label: string): string[] {
  const s: Record<string, string[]> = { ID: ['1', '2', '3'], Day: ['Monday 5 Oct', 'Monday 5 Oct', 'Tuesday 6 Oct'], Priority: ['A', 'C', 'A'],
    Business: ['Northline Barbers', 'Kalm Studio Pilates', 'Harbour Nails'], Category: ['Barber / grooming', 'Pilates / yoga / movement', 'Beauty / nails / aesthetics'],
    'Area / route': ['West Hampstead', 'Kilburn / Brondesbury', 'Hampstead / Belsize Park'], 'Current booking platform': ['Unknown — ask owner', 'Treatwell', 'Fresha'],
    'Website / direct booking': ['northlinebarbers.example', 'Not found — use source / Google link', 'harbournails.example'], 'Google Maps': ['Open Google Maps'], 'Sales status': ['Not visited'] };
  return s[label] ?? [];
}
function mockRun(req: ImportRequest): ImportResponse {
  const names = ['Northline Barbers', 'Kalm Studio Pilates', 'Harbour Nails', 'Bluefin Physio', 'Studio Verde Hair', 'Ember Spa', 'Northline Barbers Ltd', 'Glow Lashes', '', 'Pine Yoga', 'Northline Barbers — Kilburn branch'];
  const outcomes: ImportResponse['results'][number]['outcome'][] = ['imported', 'merged', 'imported', 'imported', 'imported', 'imported', 'skipped', 'skipped', 'failed', 'imported', 'imported'];
  const reasons: string[][] = [[], [], [], [], [], [], ['duplicate_in_file'], ['exists_in_other_project'], ['business_name_missing'], [], []];
  const warnings: string[][] = [[], [], [], [], [], [], [], ['invalid_email_kept_in_notes'], [], [], ['branch_shared_contact']];
  const results = names.map((n, i) => ({
    row: 7 + i, row_ref: `${req.workbook_key ?? 'wb'}:${i + 1}`, business_name: n || null, outcome: outcomes[i], reasons: reasons[i], warnings: warnings[i],
    matched_by: outcomes[i] === 'merged' ? 'name_postcode' : outcomes[i] === 'skipped' ? (i === 6 ? 'email' : 'name_postcode') : null,
    lead_ref: req.dry_run || outcomes[i] === 'failed' || outcomes[i] === 'skipped' ? null : `L-0000${(i + 1).toString().padStart(2, '0')}`,
    preview: { business_name: n || null, modality: 'Hair / beauty', city_region: 'West Hampstead', postcode: 'NW6 3AY', website: i % 2 ? null : 'https://example.test/', business_email: null, phone: i % 3 ? null : '0207 000 0001',
      booking_platform: i % 4 ? 'Treatwell' : 'Unknown', booking_platform_kind: i % 4 ? 'directory_listing' : 'unknown', visit_approach: 'walk_in', status: 'DISCOVERED', next_action_date: null, priority: 'A', route_day: 'Monday 5 Oct', platform_evidence_url: null },
  }));
  return {
    dry_run: req.dry_run, sheet: req.sheet, header_row: 6, project_id: req.project_id, project_host: 'booksesh.com', campaign: req.campaign ?? null,
    workbook_key: req.workbook_key ?? 'booksesh-150-prospects-week1', import_batch_id: req.dry_run ? null : 'mock-batch', mapped_fields: Object.values(req.mapping),
    summary: { rows_found: 11, valid: 10, imported: 7, merged: 1, skipped: 2, failed: 1 },
    reasons: { business_name_missing: 1, duplicate_in_file: 1, exists_in_other_project: 1 },
    results,
  };
}
